import { useState, useEffect, useMemo, useCallback } from 'react';
import { Room, ROOM_CONTEXTS, RoomContext, RoomCategory, ROOM_CATEGORY_TABS, getRoomCategory } from '../types/user';
import { useRooms } from '../context/RoomContext';
import { useUser } from '../context/UserContext';
import { useCharacter } from '../context/CharacterContext';
import { Character } from '../types/character';
import { isQaRoom } from '../utils/qaRoom';
import { CreateRoomModal } from './CreateRoomModal';
import { PasswordPromptModal } from './PasswordPromptModal';
import { PendingApprovalModal } from './PendingApprovalModal';
import { JoinRequestModal } from './JoinRequestModal';
import { CharacterSelectionModal } from './CharacterSelectionModal';
import { appConfig, appMode } from '../config';
import {
  exploreService,
  ExploreRoom,
  ExplorePeek,
  ExploreUpcomingEvent,
  RankedRoom,
} from '../services/api/exploreService';
import { RoomStoryCard } from './RoomStoryCard';
import { EventRail } from './EventRail';
import { PeekSheet, PeekTarget } from './explore/PeekSheet';
import { splitNewsSections, emptyRoomInvitation } from '../utils/roomStory';
import './RoomsDashboard.css';

interface RoomsDashboardProps {
  onJoinRoom: (room: Room) => void;
}

/** Dashboard tab values: the shared catalog buckets plus a local-only QA tab. */
type DashboardTab = RoomCategory | 'qa';

/** Local-only QA tab (not added to the shared ROOM_CATEGORY_TABS — it is reused elsewhere). */
const QA_TAB = { value: 'qa' as const, label: 'QA', icon: '🧪', description: 'Test, debug & smoke rooms' };

export function RoomsDashboard({ onJoinRoom }: RoomsDashboardProps) {
  const { rooms, filteredRooms, filters, setFilters, joinRoom, requestToJoin, pendingJoinRequests, isLoading, isConnected, error, refreshRooms, currentRoom } = useRooms();
  const { user } = useUser();
  const { characters, setActiveCharacter } = useCharacter();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTemplateId, setCreateTemplateId] = useState<string | null>(null);
  const [joiningRoomId, setJoiningRoomId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<DashboardTab>('all');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [passwordPromptRoom, setPasswordPromptRoom] = useState<Room | null>(null);
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [pendingApprovalRoom, setPendingApprovalRoom] = useState<Room | null>(null);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [showJoinRequestsModal, setShowJoinRequestsModal] = useState(false);
  const [characterSelectRoom, setCharacterSelectRoom] = useState<Room | null>(null);

  // News-front-page data (V2 Amethyst WU-03): the gated ExploreController projection
  // (real photo/name only per peek-visibility) drives every card on this page —
  // the raw SignalR `rooms` list is used only for join-mechanics + host-name lookups.
  const isClassroom = appMode === 'classroom';
  const [exploreRooms, setExploreRooms] = useState<ExploreRoom[]>([]);
  const [ranked, setRanked] = useState<{ basis: string; rooms: RankedRoom[] } | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<ExploreUpcomingEvent[]>([]);
  const [peekTarget, setPeekTarget] = useState<PeekTarget | null>(null);
  const [peek, setPeek] = useState<ExplorePeek | null>(null);
  const [peekLoading, setPeekLoading] = useState(false);

  // Re-render every 30s so time-derived UI (event "starting soon" chip, "started Xm
  // ago" bylines) stays fresh without a full data refetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30000);
    return () => window.clearInterval(id);
  }, []);

  // A room's occupant count only reaches this client via SignalR if it's
  // already joined that room's group (RoomHub only broadcasts
  // PlayerJoinedRoom/PlayerLeftRoom to `room:{roomId}`, not to every connected
  // client) — a card for someone else's room otherwise never learns about a
  // join/leave. `rooms.length` alone also misses it (that only changes when
  // the *number of rooms* changes). Poll on a short interval so cards catch up
  // without requiring a remount.
  const [occupancyPollTick, setOccupancyPollTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setOccupancyPollTick((t) => t + 1), 8000);
    return () => window.clearInterval(id);
  }, []);

  const fetchExploreRooms = useCallback(() => {
    return exploreService.getExploreRooms({ category: 'all', includeQa: true }).then(setExploreRooms);
  }, []);

  useEffect(() => {
    if (!isConnected) { setExploreRooms([]); return; }
    let cancelled = false;
    exploreService.getExploreRooms({ category: 'all', includeQa: true }).then((rms) => {
      if (!cancelled) setExploreRooms(rms);
    });
    return () => { cancelled = true; };
  }, [isConnected, rooms.length, occupancyPollTick]);

  const nonQaExplore = useMemo(() => exploreRooms.filter((r) => !isQaRoom(r.name)), [exploreRooms]);
  const exploreById = useMemo(() => new Map(exploreRooms.map((r) => [r.lobbyId, r])), [exploreRooms]);

  // WU-05 ranking seam: real relevance when the reco surface is deployed,
  // occupancy-sort fallback otherwise (getRankedRooms's own contract).
  useEffect(() => {
    if (nonQaExplore.length === 0) { setRanked(null); return; }
    let cancelled = false;
    exploreService.getRankedRooms(nonQaExplore).then((res) => {
      if (!cancelled) setRanked(res);
    });
    return () => { cancelled = true; };
  }, [nonQaExplore]);

  useEffect(() => {
    if (!isConnected) return;
    let cancelled = false;
    exploreService.getUpcomingEvents({ scope: isClassroom ? 'classroom' : 'dating', withinHours: 24 }).then((evts) => {
      if (!cancelled) setUpcomingEvents(evts);
    });
    return () => { cancelled = true; };
  }, [isConnected, isClassroom]);

  const sections = useMemo(() => {
    const source: RankedRoom[] = ranked?.rooms ?? nonQaExplore.map((r) => ({ ...r, relevanceReasons: [] }));
    return splitNewsSections(source, { liveNowSize: 4, forYouSize: 4 });
  }, [ranked, nonQaExplore]);

  const hostNameFor = useCallback(
    (lobbyId: string): string | null => rooms.find((r) => r.id === lobbyId)?.hostName ?? null,
    [rooms],
  );

  const handleJoinRoom = async (room: Room, password?: string, character?: Character) => {
    if (!user) return;

    // If room has password and we don't have one yet, show prompt
    if (room.hasPassword && !password) {
      setPasswordPromptRoom(room);
      setPasswordError(undefined);
      return;
    }

    // If user has multiple characters and hasn't selected one yet, show character selection
    if (characters.length > 1 && !character && !characterSelectRoom) {
      setCharacterSelectRoom(room);
      return;
    }

    setJoiningRoomId(room.id);
    setJoinError(null);

    try {
      // Use selected character if available, otherwise use user's default profile
      const charToUse = character || characters.find(c => c.isActive) || characters[0];

      // B8: activate the chosen character server-side BEFORE joining so the
      // avatar/appearance the room spawns matches the selection. Non-blocking:
      // a failed activation must not stop the join.
      if (charToUse && !charToUse.isActive) {
        try {
          await setActiveCharacter(charToUse.id);
        } catch (activateErr) {
          console.warn('Failed to activate character before join:', activateErr);
        }
      }

      let characterType: string;
      let gender: string;
      let appearanceData: string | undefined;

      if (charToUse) {
        // Use character data
        const genderSuffix = charToUse.gender === 'female' ? 'Girl' : 'Boy';
        characterType = `${charToUse.characterClass}_${genderSuffix}`;
        gender = charToUse.gender;
        appearanceData = charToUse.appearanceData;
      } else {
        // Fallback to user profile
        const genderSuffix = user.gender === 'female' ? 'Girl' : 'Boy';
        characterType = `${user.characterClass}_${genderSuffix}`;
        gender = user.gender || 'male';
      }

      const success = await joinRoom(room.id, user.id, user.displayName, user.avatar, characterType, gender, password, appearanceData);
      if (success) {
        setPasswordPromptRoom(null);
        setCharacterSelectRoom(null);
        setPeekTarget(null);
        onJoinRoom(room);
      } else {
        setJoinError('Failed to join room. Please try again.');
      }
    } catch (e: unknown) {
      const err = e as Error;
      if (err.message === 'PASSWORD_REQUIRED') {
        // Show password prompt
        setPasswordPromptRoom(room);
        setPasswordError(undefined);
      } else if (err.message === 'INVALID_PASSWORD') {
        // Show error in password modal
        setPasswordError('INVALID_PASSWORD');
      } else if (err.message === 'APPROVAL_REQUIRED') {
        // Room requires approval - send join request
        try {
          const request = await requestToJoin(room.id, user.id, user.displayName, user.avatar);
          setPendingRequestId(request.id);
          setPendingApprovalRoom(room);
        } catch (requestErr) {
          console.error('Failed to send join request:', requestErr);
          setJoinError('Failed to request to join. Please try again.');
        }
      } else {
        console.error('Failed to join room:', e);
        setJoinError('Failed to join room. Please try again.');
        setPasswordPromptRoom(null);
      }
    } finally {
      setJoiningRoomId(null);
    }
  };

  // Peek (observe) — the card body's primary gesture; Join lives in the sheet.
  const openPeek = useCallback((exploreRoom: ExploreRoom) => {
    setPeekTarget({ kind: 'room', room: exploreRoom });
    setPeek(null);
    setPeekLoading(true);
    exploreService.peekRoom(exploreRoom.lobbyId).then(setPeek).finally(() => setPeekLoading(false));
  }, []);

  // Resolve an ExploreRoom back to its live SignalR Room so the real join path runs unchanged.
  const joinFromExplore = useCallback((exploreRoom: ExploreRoom) => {
    const liveRoom = rooms.find((r) => r.id === exploreRoom.lobbyId);
    if (!liveRoom) {
      setJoinError('That room just slipped away — try refreshing.');
      setPeekTarget(null);
      return;
    }
    handleJoinRoom(liveRoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);

  // Tonight rail: an event only has a template, not a live lobby. Before it
  // starts there is nothing to join (no reservation logic, per spec); at/after
  // start, prefer an already-live room on that template, else open the
  // create-room flow preselected to it (same "open this world" idiom Explore uses).
  const openEventTemplate = useCallback((event: ExploreUpcomingEvent) => {
    if (!event.sceneTemplateId) return;
    const liveMatch = rooms.find((r) => r.sceneTemplateId === event.sceneTemplateId && !isQaRoom(r.name));
    if (liveMatch) {
      handleJoinRoom(liveMatch);
      return;
    }
    setCreateTemplateId(event.sceneTemplateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);

  // The visible "Refresh" button previously only re-synced the raw SignalR
  // room list (`refreshRooms`), which never touches the ExploreController-
  // backed card data — clicking it did nothing for a stale occupancy count.
  const handleRefreshClick = useCallback(() => {
    refreshRooms();
    fetchExploreRooms();
  }, [refreshRooms, fetchExploreRooms]);

  const handleCharacterSelected = (character: Character) => {
    if (characterSelectRoom) {
      handleJoinRoom(characterSelectRoom, undefined, character);
    }
  };

  const handleCharacterSelectCancel = () => {
    setCharacterSelectRoom(null);
  };

  const handlePasswordSubmit = (password: string) => {
    if (passwordPromptRoom) {
      handleJoinRoom(passwordPromptRoom, password);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordPromptRoom(null);
    setPasswordError(undefined);
  };

  const handleApprovalCancel = () => {
    setPendingApprovalRoom(null);
    setPendingRequestId(null);
  };

  const handleApproved = (room: Room) => {
    setPendingApprovalRoom(null);
    setPendingRequestId(null);
    onJoinRoom(room);
  };

  const handleDenied = (reason?: string) => {
    setPendingApprovalRoom(null);
    setPendingRequestId(null);
    setJoinError(reason ? `Request denied: ${reason}` : 'Your request to join was denied.');
  };

  // Dating-only UI (dating tab + its copy) is hidden in classroom mode.
  // The 🧪 QA tab is appended locally (never mutates the shared ROOM_CATEGORY_TABS).
  const categoryTabs: { value: DashboardTab; label: string; icon: string; description: string }[] = isClassroom
    ? [...ROOM_CATEGORY_TABS.filter(tab => tab.value !== 'dating'), QA_TAB]
    : [...ROOM_CATEGORY_TABS, QA_TAB];

  // Split QA/test rooms out so they never pollute the real category views.
  const nonQaRooms = filteredRooms.filter(room => !isQaRoom(room.name));
  const qaRooms = filteredRooms.filter(room => isQaRoom(room.name));

  // Filter rooms by selected category. QA tab = QA rooms only; every other tab
  // uses the QA-excluded pool (server-denormalized category, context fallback).
  const categoryFilteredRooms = selectedCategory === 'qa'
    ? qaRooms
    : selectedCategory === 'all'
      ? nonQaRooms
      : nonQaRooms.filter(room => getRoomCategory(room) === selectedCategory);

  // Count rooms per tab for the badges (QA excluded from the real buckets).
  const roomCounts: Record<DashboardTab, number> = {
    all: nonQaRooms.length,
    games: 0,
    lobbies: 0,
    interests: 0,
    dating: 0,
    qa: qaRooms.length,
  };
  for (const room of nonQaRooms) {
    roomCounts[getRoomCategory(room)] += 1;
  }

  return (
    <div className="rooms-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          {/* Mode-aware branding from the active preset (GameDate / Quest Academy) */}
          <h1>{appConfig.name}</h1>
          <p>{appConfig.description}</p>
        </div>
        <div className="header-actions">
          {!isConnected && (
            <span className="connection-indicator disconnected">
              Offline
            </span>
          )}
          {isConnected && (
            <span className="connection-indicator connected">
              Connected
            </span>
          )}
          <button className="refresh-btn" onClick={handleRefreshClick} disabled={!isConnected || isLoading}>
            Refresh
          </button>
          <button className="create-room-btn" onClick={() => setShowCreateModal(true)} disabled={!isConnected}>
            + Create Room
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={refreshRooms}>Retry</button>
        </div>
      )}

      {joinError && (
        <div className="error-banner">
          {joinError}
          <button onClick={() => setJoinError(null)}>Dismiss</button>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading rooms...</p>
        </div>
      ) : (
        <>
          {sections.hero && (
            <section className="dashboard-hero">
              <RoomStoryCard
                variant="hero"
                room={sections.hero}
                isClassroom={isClassroom}
                hostName={hostNameFor(sections.hero.lobbyId)}
                onPeek={() => openPeek(sections.hero as ExploreRoom)}
                onJoin={() => joinFromExplore(sections.hero as ExploreRoom)}
                joining={joiningRoomId === sections.hero.lobbyId}
              />
            </section>
          )}

          {sections.liveNow.length > 0 && (
            <section className="dashboard-section">
              <h2 className="dashboard-section-title">
                <span className="dashboard-section-glyph" aria-hidden="true">▶</span> Live now
              </h2>
              <div className="dashboard-medium-rail">
                {sections.liveNow.map((r) => (
                  <RoomStoryCard
                    key={r.lobbyId}
                    variant="medium"
                    room={r}
                    isClassroom={isClassroom}
                    hostName={hostNameFor(r.lobbyId)}
                    onPeek={() => openPeek(r)}
                  />
                ))}
              </div>
            </section>
          )}

          {upcomingEvents.length > 0 && (
            <section className="dashboard-section">
              <h2 className="dashboard-section-title">
                <span className="dashboard-section-glyph" aria-hidden="true">✨</span> Tonight
              </h2>
              <EventRail events={upcomingEvents} onOpen={openEventTemplate} />
            </section>
          )}

          {sections.forYou.length > 0 && (
            <section className="dashboard-section">
              <h2 className="dashboard-section-title">
                <span className="dashboard-section-glyph" aria-hidden="true">💌</span> For you
              </h2>
              <div className="dashboard-medium-rail">
                {sections.forYou.map((r) => (
                  <RoomStoryCard
                    key={r.lobbyId}
                    variant="medium"
                    room={r}
                    isClassroom={isClassroom}
                    hostName={hostNameFor(r.lobbyId)}
                    invitation={emptyRoomInvitation(r)}
                    onPeek={() => openPeek(r)}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="dashboard-section dashboard-browse-all">
            <h2 className="dashboard-section-title">
              <span className="dashboard-section-glyph" aria-hidden="true">🗂️</span> Browse all
            </h2>

            {/* Room Category Tabs */}
            <div className="room-category-tabs">
              {categoryTabs.map(tab => (
                <button
                  key={tab.value}
                  className={`category-tab ${selectedCategory === tab.value ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(tab.value)}
                  title={tab.description}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-label">{tab.label}</span>
                  <span className="tab-count">{roomCounts[tab.value]}</span>
                </button>
              ))}
            </div>

            {/* Gaming Optional Banner */}
            {selectedCategory === 'dating' && (
              <div className="info-banner gaming-optional">
                <span className="banner-icon">💡</span>
                <span className="banner-text">
                  <strong>Gaming is optional!</strong> These rooms focus on meeting people. No game character required.
                </span>
              </div>
            )}

            {selectedCategory === 'games' && (
              <div className="info-banner gaming-info">
                <span className="banner-icon">🎮</span>
                <span className="banner-text">
                  <strong>Game rooms</strong> - Meet people while playing together. Your character will join the adventure!
                </span>
              </div>
            )}

            {selectedCategory === 'interests' && (
              <div className="info-banner gaming-info">
                <span className="banner-icon">🎯</span>
                <span className="banner-text">
                  <strong>Interest rooms</strong> - Topic-themed hangouts built around what you love.
                </span>
              </div>
            )}

            <div className="filters-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search rooms..."
                  value={filters.search}
                  onChange={(e) => setFilters({ search: e.target.value })}
                />
              </div>

              <div className="filter-group">
                <select
                  value={filters.context}
                  onChange={(e) => setFilters({ context: e.target.value as RoomContext | 'all' })}
                >
                  <option value="all">All Types</option>
                  {ROOM_CONTEXTS.map(ctx => (
                    <option key={ctx.value} value={ctx.value}>{ctx.label}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group age-filter">
                <label>Age:</label>
                <input
                  type="number"
                  value={filters.minAge}
                  onChange={(e) => setFilters({ minAge: parseInt(e.target.value) || 18 })}
                  min={18}
                  max={99}
                />
                <span>-</span>
                <input
                  type="number"
                  value={filters.maxAge}
                  onChange={(e) => setFilters({ maxAge: parseInt(e.target.value) || 99 })}
                  min={18}
                  max={99}
                />
              </div>

              <label className="checkbox-filter">
                <input
                  type="checkbox"
                  checked={filters.showFull}
                  onChange={(e) => setFilters({ showFull: e.target.checked })}
                />
                Show full rooms
              </label>
            </div>

            <div className="dashboard-row-list">
              {categoryFilteredRooms.length === 0 ? (
                <div className="no-rooms">
                  <p>{isConnected ? `No ${selectedCategory === 'all' ? '' : selectedCategory + ' '}rooms match your filters` : 'Connect to see available rooms'}</p>
                  {isConnected && <button onClick={() => setShowCreateModal(true)}>Create one!</button>}
                </div>
              ) : (
                categoryFilteredRooms.map((room) => {
                  const exploreRoom = exploreById.get(room.id);
                  if (!exploreRoom) return null;
                  return (
                    <RoomStoryCard
                      key={room.id}
                      variant="row"
                      room={exploreRoom}
                      isClassroom={isClassroom}
                      hostName={room.hostName}
                      onPeek={() => openPeek(exploreRoom)}
                    />
                  );
                })
              )}
            </div>
          </section>
        </>
      )}

      {peekTarget && (
        <PeekSheet
          target={peekTarget}
          peek={peek}
          peekLoading={peekLoading}
          joining={joiningRoomId !== null}
          hostName={peekTarget.kind === 'room' ? hostNameFor(peekTarget.room.lobbyId) : null}
          onClose={() => setPeekTarget(null)}
          onJoin={joinFromExplore}
          onOpenWorld={() => { /* RoomsDashboard peeks rooms only, never templates. */ }}
        />
      )}

      {showCreateModal && (
        <CreateRoomModal onClose={() => setShowCreateModal(false)} />
      )}

      {createTemplateId !== null && (
        <CreateRoomModal initialSceneTemplateId={createTemplateId} onClose={() => setCreateTemplateId(null)} />
      )}

      {passwordPromptRoom && (
        <PasswordPromptModal
          roomName={passwordPromptRoom.name}
          onSubmit={handlePasswordSubmit}
          onCancel={handlePasswordCancel}
          error={passwordError}
          isSubmitting={joiningRoomId === passwordPromptRoom.id}
        />
      )}

      {pendingApprovalRoom && pendingRequestId && (
        <PendingApprovalModal
          room={pendingApprovalRoom}
          requestId={pendingRequestId}
          onCancel={handleApprovalCancel}
          onApproved={handleApproved}
          onDenied={handleDenied}
        />
      )}

      {showJoinRequestsModal && currentRoom && (
        <JoinRequestModal
          requests={pendingJoinRequests}
          roomId={currentRoom.id}
          onClose={() => setShowJoinRequestsModal(false)}
        />
      )}

      {characterSelectRoom && characters.length > 1 && (
        <CharacterSelectionModal
          characters={characters}
          onSelect={handleCharacterSelected}
          onCancel={handleCharacterSelectCancel}
          isLoading={joiningRoomId === characterSelectRoom.id}
        />
      )}
    </div>
  );
}
